/**
 * The assistant's data (docs/ASSISTANT.md). Screens import this, never the
 * server. Approving a change can change records anywhere, so it refreshes them.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { orpc } from '@/adapters/rpc/client'
import type { DisplayMessage } from '@/core/assistant'
import { useInvalidate } from '@/features/shared/hooks/use-invalidate'

export function useAssistantStatus(enabled: boolean) {
  return useQuery({ ...orpc.assistant.status.queryOptions(), enabled, staleTime: 0 })
}

export function useConversations(enabled: boolean) {
  return useQuery({ ...orpc.assistant.conversations.queryOptions(), enabled })
}

export function useConversation(conversationId: string | null) {
  return useQuery({
    ...orpc.assistant.messages.queryOptions({ input: { conversationId: conversationId ?? '' } }),
    enabled: Boolean(conversationId),
  })
}

function useStore() {
  const queryClient = useQueryClient()
  return (conversationId: string, messages: DisplayMessage[]) => {
    queryClient.setQueryData(
      orpc.assistant.messages.queryKey({ input: { conversationId } }),
      messages,
    )
    void queryClient.invalidateQueries({ queryKey: orpc.assistant.conversations.key() })
    void queryClient.invalidateQueries({ queryKey: orpc.assistant.status.key() })
  }
}

export function useSend() {
  const store = useStore()
  return useMutation(
    orpc.assistant.send.mutationOptions({
      onSuccess: ({ conversationId, messages }) => store(conversationId, messages),
    }),
  )
}

export function useDecide() {
  const store = useStore()
  const invalidateItems = useInvalidate('items')
  const approve = useMutation(orpc.assistant.approve.mutationOptions())
  const decline = useMutation(orpc.assistant.decline.mutationOptions())
  return {
    busy: approve.isPending || decline.isPending,
    error: approve.error ?? decline.error,
    async decide(conversationId: string, yes: boolean) {
      const messages = await (yes ? approve : decline).mutateAsync({ conversationId })
      store(conversationId, messages)
      // An approved change may have touched any record on screen.
      if (yes) void invalidateItems()
    },
  }
}
